import {Bygginformasjon} from "./Bygginformasjon";
import {Klientinformasjon, KlientinformasjonFraUserAgent} from "../tjenester/KlientinformasjonFraUserAgent";
import {UiRouterFasade} from "./UiRouterFasade";
import {Konfigurasjon} from "./Konfigurasjon";
import {nyUuid} from "../../felles-ng2/støttefunksjoner/uuidGenerator";
import {initDebugger} from "../../felles-ng2/støttefunksjoner/debug";

declare const device: any;

const debug = initDebugger('StandardRequestHeadereHttpInterceptorAvhengigheter');

/** Ajs basert implementasjon av StandardRequestHeadereHttpInterceptor avhengigheter
 * NB: Denne ble flyttet tilbake tl felles/tjenester fra ng2-treet for å begrense bundle-størrelse i mobil-appen (unngå bundling av ng2/Angular i app).
 *
 */
export class StandardRequestHeadereHttpInterceptorAvhengigheter {

    private nettleserinfo: Klientinformasjon;

    constructor(private konfigurasjon: Konfigurasjon,
                private bygginformasjon: Bygginformasjon,
                private klientinformasjon: KlientinformasjonFraUserAgent,
                private uiRouter: UiRouterFasade) {
        this.nettleserinfo = this.klientinformasjon.kallendeklientinformasjon();
    }
    static forInjisering = [Konfigurasjon.regNavn, Bygginformasjon.regNavn, KlientinformasjonFraUserAgent.regNavn, UiRouterFasade.regNavn, StandardRequestHeadereHttpInterceptorAvhengigheter];
    static regNavn = "standardRequestHeadereInterceptorAvhengigheter";

    statiskInformasjon(): StatiskeStandardRequestHeadere {
        return {
            klientVersjon: this.bygginformasjon.version,
            klientId: this.konfigurasjon.klientId,
            klientType: this.konfigurasjon.klientType,
            platformNavn: this.platformNavn(),
            platformVersjon: this.platformVersjon(),
            deviceNavn: this.deviceNavn(),
            deviceId: this.deviceId(),
            miljø: this.konfigurasjon.miljø
        };
    }

    kallInformasjon(): KallspesifikkeStandardRequestHeadere {
        return {
            stateUrl: this.uiRouter.currentState()?.url ?? 'ukjent',
            stateName: this.uiRouter.currentState()?.name ?? 'ukjent'
        };
    }

    private platformNavn() {
        if (window['device']) return device.platform;
        return this.nettleserinfo.osNavn;
    }

    private platformVersjon() {
        if (window['device']) return device.version;
        return this.nettleserinfo.osVersjon;
    }

    private deviceNavn() {
        if (window['device']) return device.model;
        return this.nettleserinfo.nettleserNavn;
    }

    private deviceId() {
        if (window['device']) return device.uuid;
        return '';
    }
}

/**
* Registrerer standard headere som serverside benytter ifbm logging.
* <pre>
* Merk at denne metoden kalles fra både ajs (lederplan-klient/src/scripts/felles/infrastruktur/StandardRequestHeadereInterceptor.ts)
* og ng2 (lederplan-klient/src/scripts/felles-ng2/infrastruktur/http/StandardRequestHeadereHttpInterceptor.ts)
*
* ########################################################################
* MERK - DERSOM DU LEGGER TIL HEADERE HER MÅ DISSE OGSÅ LEGGES TIL I
* no.systemfabrikken.lederplan.infrastruktur.filter.LPCrossOriginFilter
* ########################################################################
* </pre>
*/
export function registrerStandardRequestHeadere(
  statiskInformasjon: StatiskeStandardRequestHeadere,
  kallInformasjon: KallspesifikkeStandardRequestHeadere,
  registrerHeader: (navn: string, verdi: string) => void) {
    const headere = new Map([
        [StandardRequestHeadere.lpStateUrl, kallInformasjon.stateUrl],
        [StandardRequestHeadere.lpStateName, kallInformasjon.stateName],
        [StandardRequestHeadere.lpKlientVer, statiskInformasjon.klientVersjon],
        [StandardRequestHeadere.lpKlientId, statiskInformasjon.klientId],
        [StandardRequestHeadere.lpMiljoe, statiskInformasjon.miljø],
        [StandardRequestHeadere.lpKlient, statiskInformasjon.klientType], //web eller mobil
        [StandardRequestHeadere.lpForespoerselId, nyUuid()],
        [StandardRequestHeadere.lpPlatform, statiskInformasjon.platformNavn], //osnavn, feks ios/android/macOS etc
        [StandardRequestHeadere.lpPlatformVer, statiskInformasjon.platformVersjon], //osversjon, feks 10.3.1
        [StandardRequestHeadere.lpDevice, statiskInformasjon.deviceNavn], //enten nettlesernavn (på web) eller devicenavn på mobil
        [StandardRequestHeadere.lpDeviceId, statiskInformasjon.deviceId] //cordova.device.uuid på mobil ellers tom
    ]);
    debug('registrerer standard request-header',headere);
    for (const [nøkkel, verdi] of headere)
        registrerHeader(nøkkel, verdi);
}

export enum StandardRequestHeadere {
    lpStateUrl = 'lpStateUrl',
    lpStateName = 'lpStateName',
    lpKlientVer = 'lpKlientVer',
    lpKlientId = 'lpKlientId',
    lpMiljoe = 'lpMiljoe',
    lpKlient = 'lpKlient',
    lpForespoerselId = 'lpForespoerselId',
    lpPlatform = 'lpPlatform',
    lpPlatformVer = 'lpPlatformVer',
    lpDevice = 'lpDevice',
    lpDeviceId = 'lpDeviceId'
}

export interface StatiskeStandardRequestHeadere {
    klientVersjon: string;
    klientId: string;
    klientType: string;
    platformNavn: string;
    platformVersjon: string;
    deviceNavn: string;
    deviceId: string;
    miljø: string;
}

export interface KallspesifikkeStandardRequestHeadere {
    stateUrl: string,
    stateName: string
}
